import React, { useEffect, useMemo, useRef, useState } from "react";
import Modal from "../Modal/Modal";
import { useVerificationMethodContext } from "./verificationMethodContext";
import styled from "styled-components";
import FormButton from "../FormCustom2/Button";
import { OTP_METHOD } from "../../utils/otpMethod";
import { InputOtp } from "../TwoFA/style";
import { LineIcon } from "../Icons/LineIcon";
import { IconOpenEye } from "../Icons/IconOpenEye";
import { IconCloseEye } from "../Icons/IconCloseEye";
import { VERIFICATION_STEP } from "./constant";
import { useModalConfirm } from "../ModalConfirm/useModalConfirm";
import { useNavigate } from "react-router-dom";
import { ssoGetUserInfo } from "../../services/app";
import { OTPInput } from "components/OTPInput";

const ContentWrapper = styled.div`
  max-height: 80vh;
  overflow-y: auto;
  &::-webkit-scrollbar {
    display: none;
  }
  .input-otp--box {
    width: 100%;
    input {
      border: 1px solid #d4d4d4 !important;
      padding: 12px 8px !important;
      border-radius: 8px;
      min-width: 36px !important;
      width: 100% !important;
      max-width: 44px !important;
      font-weight: bold;
      &:focus {
        border-color: #000000 !important;
      }
    }
  }
`;

const VerificationMethodItemStyled = styled.label`
  input[type="radio"]:checked ~ .verification-method--item__content {
    border-color: #43a047;
    background-color: #43a0470d;
    .verification-method--item__content__mark {
      opacity: 1;
    }
  }
`;

const VerificationStepStyled = styled.div`
  .verification-step__item {
    .verification-step__item__title {
      outline: 2px solid transparent;
      background-color: #a3a3a3;
    }
    .verification-step__item__text {
      color: #a3a3a3;
    }
    &--active {
      .verification-step__item__title {
        outline: 2px solid #dfdfdf;
        background-color: #000;
      }
      .verification-step__item__text {
        color: #000;
      }
    }
    .verification-step__item__title--success {
      background-color: #16a34a;
      outline: 2px solid transparent;
    }
  }
`;

export const ModalVerificationMethod = ({ onClose, onSubmit, loading }) => {
  const {
    user,
    T,
    selectedMethod,
    helpLink,
    setGlobalLoading,
    setIsShowModal,
    lineVerification,
    emailVerification,
  } = useVerificationMethodContext();
  const [verificationMethod, setVerificationMethod] = useState();
  const modalConfirm = useModalConfirm();
  const navigate = useNavigate();

  /**
   * https://developers.line.biz/en/docs/line-login/integrate-line-login/#making-an-authorization-request
   */
  const getLineLoginUrl = () => {
    const baseUrl = process.env.REACT_APP_LINE_LOGIN_URL;
    const clientId = process.env.REACT_APP_LINE_CLIENT_ID;
    const redirectUri = process.env.REACT_APP_LINE_REDIRECT_URI;
    const state = "a2v-line-1752204462471";
    const scope = ["openid", "profile"].join("%20");
    const bot_prompt = "aggressive";
    const prompt = "consent";
    return `${baseUrl}?response_type=code&client_id=${clientId}&redirect_uri=${redirectUri}&state=${state}&scope=${scope}&prompt=${prompt}&bot_prompt=${bot_prompt}`;
  };

  const btnConnectLineOnClick = async () => {
    const hasPassword = await checkUserHasPassword();
    if (hasPassword) {
      const lineLoginUrl = getLineLoginUrl();
      // save current href to localstorage
      localStorage.setItem("href_before_connect_line", window.location.href);
      // save current state
      localStorage.setItem(
        "state_before_connect_line",
        JSON.stringify({
          selectedMethod,
          emailVerification,
          lineVerification,
        })
      );

      // Open the LINE login URL in a new tab
      window.location.href = lineLoginUrl;
    } else {
      handleSetPassword();
    }
  };

  const checkUserHasPassword = async () => {
    try {
      setGlobalLoading(true);
      const res = await ssoGetUserInfo();
      return Boolean(res?.data?.data?.has_password);
    } catch (error) {
      console.error("Error checking user password:", error);
      return true;
    } finally {
      setGlobalLoading(false);
    }
  };

  const handleSetPassword = async () => {
    const confirm = await modalConfirm.confirm({
      title: T("Set password"),
      message: T("Kindly create a password before linking your LINE account"),
      confirmButtonTitle: T("OK"),
      cancelButtonTitle: T("Cancel"),
    });
    if (confirm) {
      setIsShowModal(false);
      navigate("/change-password");
    }
  };

  const otpMethods = useMemo(
    () => [
      {
        value: OTP_METHOD.EMAIL,
        icon: (
          <svg
            width="1em"
            height="1em"
            viewBox="0 0 32 33"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M2.71875 8.5957L11.7436 13.7092C15.0707 15.5944 16.4765 15.5944 19.8036 13.7092L28.8284 8.5957"
              stroke="#222222"
              strokeWidth="1.95822"
              strokeLinejoin="round"
            />
            <path
              d="M2.73933 18.355C2.82468 22.357 2.86735 24.358 4.34402 25.8403C5.82069 27.3226 7.87584 27.3742 11.9862 27.4775C14.5194 27.5412 17.0278 27.5412 19.561 27.4775C23.6713 27.3742 25.7265 27.3226 27.2031 25.8403C28.6798 24.358 28.7225 22.357 28.8078 18.355C28.8353 17.0682 28.8353 15.7891 28.8078 14.5023C28.7225 10.5003 28.6798 8.49926 27.2031 7.01697C25.7265 5.53468 23.6713 5.48305 19.561 5.37977C17.0277 5.31612 14.5194 5.31612 11.9861 5.37976C7.87584 5.48303 5.82069 5.53466 4.34401 7.01695C2.86734 8.49924 2.82467 10.5002 2.73933 14.5022C2.71189 15.789 2.71189 17.0682 2.73933 18.355Z"
              stroke="#222222"
              strokeWidth="1.95822"
              strokeLinejoin="round"
            />
          </svg>
        ),
        title: (
          <>
            <div className="flex flex-col">
              <div className="text-[14px] lg:text-[20px] font-semibold">
                {T("Send to your email")}
              </div>
              <div className="text-[12px]/[15px] lg:text-[16px] text-[#000000B2]">
                {user?.email}
              </div>
            </div>
          </>
        ),
      },
      {
        value: OTP_METHOD.LINE,
        disabled: !user?.lineUserId,
        icon: (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 48 48"
            width="1em"
            height="1em"
          >
            <path
              fill="#00c300"
              transform="scale(1.2) translate(-4,-4)"
              d="M12.5,42h23c3.59,0,6.5-2.91,6.5-6.5v-23C42,8.91,39.09,6,35.5,6h-23C8.91,6,6,8.91,6,12.5v23C6,39.09,8.91,42,12.5,42z"
            />
            <path
              fill="#fff"
              transform="scale(1.2) translate(-4,-4)"
              d="M37.113,22.417c0-5.865-5.88-10.637-13.107-10.637s-13.108,4.772-13.108,10.637c0,5.258,4.663,9.662,10.962,10.495c0.427,0.092,1.008,0.282,1.155,0.646c0.132,0.331,0.086,0.85,0.042,1.185c0,0-0.153,0.925-0.187,1.122c-0.057,0.331-0.263,1.296,1.135,0.707c1.399-0.589,7.548-4.445,10.298-7.611h-0.001C36.203,26.879,37.113,24.764,37.113,22.417z M18.875,25.907h-2.604c-0.379,0-0.687-0.308-0.687-0.688V20.01c0-0.379,0.308-0.687,0.687-0.687c0.379,0,0.687,0.308,0.687,0.687v4.521h1.917c0.379,0,0.687,0.308,0.687,0.687C19.562,25.598,19.254,25.907,18.875,25.907z M21.568,25.219c0,0.379-0.308,0.688-0.687,0.688s-0.687-0.308-0.687-0.688V20.01c0-0.379,0.308-0.687,0.687-0.687s0.687,0.308,0.687,0.687V25.219z M27.838,25.219c0,0.297-0.188,0.559-0.47,0.652c-0.071,0.024-0.145,0.036-0.218,0.036c-0.215,0-0.42-0.103-0.549-0.275l-2.669-3.635v3.222c0,0.379-0.308,0.688-0.688,0.688c-0.379,0-0.688-0.308-0.688-0.688V20.01c0-0.296,0.189-0.558,0.47-0.652c0.071-0.024,0.144-0.035,0.218-0.035c0.214,0,0.42,0.103,0.549,0.275l2.67,3.635V20.01c0-0.379,0.309-0.687,0.688-0.687c0.379,0,0.687,0.308,0.687,0.687V25.219z M32.052,21.927c0.379,0,0.688,0.308,0.688,0.688c0,0.379-0.308,0.687-0.688,0.687h-1.917v1.23h1.917c0.379,0,0.688,0.308,0.688,0.687c0,0.379-0.309,0.688-0.688,0.688h-2.604c-0.378,0-0.687-0.308-0.687-0.688v-2.603c0-0.001,0-0.001,0-0.001c0,0,0-0.001,0-0.001v-2.601c0-0.001,0-0.001,0-0.002c0-0.379,0.308-0.687,0.687-0.687h2.604c0.379,0,0.688,0.308,0.688,0.687s-0.308,0.687-0.688,0.687h-1.917v1.23H32.052z"
            />
          </svg>
        ),
        title: (
          <>
            <div className="flex flex-col">
              <div className="text-[14px] lg:text-[20px] font-semibold">
                {T("Send a LINE message")}
              </div>
              <div className="text-[12px]/[15px] lg:text-[16px] text-[#000000B2]">
                {user?.lineUserId
                  ? user?.lineDisplayName
                  : T("Set up for LINE verification.")}
              </div>
            </div>
          </>
        ),
        content: <></>,
        setupButton: (
          <div className="self-center">
            <button
              className="h-[34px] px-3 bg-[#000] text-white text-[12px] rounded-full flex items-center justify-center gap-1 font-semibold hover:bg-[#333] transition-colors"
              onClick={btnConnectLineOnClick}
            >
              {T("Setup now")}
            </button>
          </div>
        ),
      },
    ],
    [user, T]
  );

  return (
    <Modal
      onClose={() => {
        onClose && onClose();
      }}
    >
      <Modal.ModalBg2
        onClose={() => {
          onClose && onClose();
        }}
      />
      <Modal.Container2
        width="650px"
        onClose={() => {
          onClose && onClose();
        }}
      >
        <ContentWrapper>
          <div className="text-center font-bold text-[18px] lg:text-[24px]">
            {T("Verification method")}
          </div>
          <div className="mt-[10px] lg:mt-[10px]">
            <div>
              <div className="mt-[15px] lg:mt-[20px]">
                <div>{T("Please choose a method to receive the OTP:")}</div>
                <div className="mt-6">
                  <div className="form__control__item form__control__item--verification-method">
                    {otpMethods.map((method, index) => (
                      <VerificationMethodItemStyled
                        key={index}
                        htmlFor={`verification-method-${method.value}`}
                        className="verification-method--item flex items-center gap-4 mb-4 lg:mb-7"
                      >
                        <input
                          type="radio"
                          name="verification-method"
                          className="verification-method--item__control w-0 h-0 opacity-0 absolute"
                          id={`verification-method-${method.value}`}
                          value={method.value}
                          checked={verificationMethod === method.value}
                          disabled={method.disabled}
                          onChange={() => setVerificationMethod(method.value)}
                        />
                        <div className="verification-method--item__content transition-all px-4 py-2 border border-[#0000004D] rounded-[12px] cursor-pointer w-full lg:px-6 lg:py-3">
                          <div className="flex items-start justify-between gap-1">
                            <div className="flex items-start gap-3 lg:gap-4">
                              <div className="text-[24px] mt-2 lg:text-[32px]">
                                {method.icon}
                              </div>
                              <div>{T(method.title)}</div>
                            </div>
                            {!method.disabled && (
                              <div className="grow-1 shrink-0 flex flex-col items-end justify-between gap-1 lg:gap-2 mt-1">
                                <div className="">
                                  <div className="w-[6px] h-[12px] lg:w-2 lg:h-4 border-[#43A047] border-solid border-b-2 border-r-2 rotate-45 opacity-0 verification-method--item__content__mark mr-2"></div>
                                </div>
                                {user?.otpMethod === method.value && (
                                  <div className="bg-[#0000001A] text-[#00000080] rounded px-2 lg:h-[17px] lg:text-[10px]/[17px] h-[15px] text-[8px]/[15px]">
                                    {T("Default")}
                                  </div>
                                )}
                              </div>
                            )}
                            {method.disabled && <>{method.setupButton}</>}
                          </div>
                          <>{method.content}</>
                        </div>
                      </VerificationMethodItemStyled>
                    ))}
                  </div>
                </div>
                <div
                  className="text-center mt-6 text-[12px] lg:text-[14px]"
                  dangerouslySetInnerHTML={{
                    __html: T(
                      "Cannot access any method above? Please contact to our Customer Service team to update email/phone number"
                    ).replace("{url}", helpLink),
                  }}
                ></div>
              </div>
              <div className="my-[32px] hidden md:block" />
              <div className="mt-[30px] mb-[20px] md:mb-[10px] flex justify-center items-center gap-[20px] border-t border-[#0000001A] pt-6">
                <FormButton
                  className={"black2 flex-1"}
                  onClick={() => {
                    onClose && onClose();
                  }}
                >
                  {T("Cancel")}
                </FormButton>
                <FormButton
                  className={"primary2 flex-1"}
                  disabled={!verificationMethod || loading}
                  onClick={() => onSubmit(verificationMethod)}
                >
                  {T("Continue")}
                </FormButton>
              </div>
            </div>
          </div>
        </ContentWrapper>
      </Modal.Container2>
    </Modal>
  );
};

export const ModalEmailVerification = ({
  onClose,
  onChangeMethod,
  onSubmitOtp,
  onSubmitTwoFa,
  onResend,
  loadingResendOtp,
  loading
}) => {
  const {
    T,
    user,
    step,
    hasTwoFa,
    emailVerification,
    setEmailVerification,
    verifyOptionParam
  } = useVerificationMethodContext();

  const [countDown, setCountDown] = useState(
    emailVerification?.duration -
      Math.floor((Date.now() - emailVerification?.otpSentAt) / 1000)
  );
  const interval = useRef(null);
  useEffect(() => {
    if (emailVerification?.duration) {
      setCountDown(emailVerification?.duration);
      interval.current = setInterval(() => {
        const now = Date.now();
        if (
          now - emailVerification?.otpSentAt >=
          emailVerification?.duration * 1000
        ) {
          clearInterval(interval.current);
          setCountDown(0);
        }
        setCountDown(
          emailVerification?.duration -
            Math.floor((now - emailVerification?.otpSentAt) / 1000)
        );
      }, 1000);
      return () => clearInterval(interval.current);
    }
  }, [emailVerification?.duration, emailVerification?.otpSentAt]);

  const onChangeVerificationMethod = () => {
    onChangeMethod && onChangeMethod();
  };

  return (
    <Modal
      onClose={() => {
        onClose && onClose();
      }}
    >
      <Modal.ModalBg2 />
      <Modal.Container2
        width="500px"
        padding="35px"
        onClose={() => {
          onClose && onClose();
        }}
      >
        <ContentWrapper>
          <div className="text-center font-bold text-[18px] lg:text-[24px]">
            {step === VERIFICATION_STEP.ENTER_OTP && (
              <span>{T("Email Verification")}</span>
            )}
            {hasTwoFa && step === VERIFICATION_STEP.ENTER_TWO_FA && (
              <span>{T("Verification")}</span>
            )}
          </div>
          <div className="mt-[15px] lg:mt-[20px]">
            {hasTwoFa && (
              <VerificationStepStyled className="flex items-center justify-center relative verification-step gap-6">
                <div
                  className={`flex flex-col items-center gap-2 verification-step__item px-5 z-10 ${
                    step === VERIFICATION_STEP.ENTER_OTP
                      ? "verification-step__item--active"
                      : ""
                  }`}
                >
                  {step === VERIFICATION_STEP.ENTER_OTP && (
                    <div className="w-8 h-8 rounded-full text-[16px]/[32px] font-bold text-white text-center verification-step__item__title">
                      1
                    </div>
                  )}
                  {emailVerification.verifiedOtp &&
                    step === VERIFICATION_STEP.ENTER_TWO_FA && (
                      <div className="w-8 h-8 rounded-full text-[16px]/[32px] font-bold text-white text-center flex items-center justify-center verification-step__item__title  verification-step__item__title--success">
                        <div className="w-2 h-4 border-b-2 border-r-2 border-white rotate-45 mb-1"></div>
                      </div>
                    )}
                  <div className="text-[16px]/[24px] font-bold verification-step__item__text">
                    {T("1st OTP")}
                  </div>
                </div>
                <div className="absolute bg-[#A3A3A3] h-[1px] w-[125px] top-4"></div>
                <div
                  className={`flex flex-col items-center gap-2 verification-step__item px-5 z-10 ${
                    step === VERIFICATION_STEP.ENTER_TWO_FA
                      ? "verification-step__item--active"
                      : ""
                  }`}
                >
                  <div className="w-8 h-8 rounded-full text-[16px]/[32px] font-bold text-white text-center verification-step__item__title">
                    2
                  </div>
                  <div className="text-[16px]/[24px] font-bold verification-step__item__text">
                    {T("2nd OTP")}
                  </div>
                </div>
              </VerificationStepStyled>
            )}
            {step === VERIFICATION_STEP.ENTER_OTP && (
              <>
                <div className="mt-[35px] flex items-center justify-between">
                  <div className="flex flex-col gap-1">
                    <span className="font-bold text-black">
                      {T("Verification code sent to email")}
                    </span>
                    <span className="text-black">{user?.email}</span>
                  </div>
                  <div>
                    {!loading &&
                      (countDown > 0 ? (
                        <div className="text-[#F12E11] text-[28px] font-bold">
                          {countDown}s
                        </div>
                      ) : (
                        <button
                          className="h-[32px] text-white bg-black px-4 py-1 rounded-full flex items-center justify-center gap-1 font-semibold text-[12px] hover:bg-[#333] transition-colors"
                          onClick={onResend}
                          disabled={loadingResendOtp}
                        >
                          {T("Resend")}
                        </button>
                      ))}
                  </div>
                </div>
                <div className="mt-5 flex items-center justify-center">
                  <OTPInput
                    value={emailVerification.otp ?? ''}
                    onChange={(otp) => {
                      setEmailVerification((cur) => ({
                        ...cur,
                        otp: otp,
                      }));
                    }}
                  />
                  {/* <InputOtp
                    value={emailVerification.otp}
                    className="input-otp--box flex items-center justify-center"
                    onChange={(otp) => {
                      setEmailVerification((cur) => ({
                        ...cur,
                        otp: otp,
                      }));
                    }}
                  /> */}
                </div>
                {emailVerification.otpError && (
                  <div className="mt-2 text-red-500">
                    {emailVerification.otpError}
                  </div>
                )}
                <div className="mt-1.5 text-[12px]">
                  <span
                    className="underline cursor-pointer"
                    onClick={onChangeVerificationMethod}
                  >
                    {T("Not received code? Change verification method")}
                  </span>
                </div>
              </>
            )}
            {hasTwoFa && step === VERIFICATION_STEP.ENTER_TWO_FA && (
              <>
                <div className="mt-6">
                  <div className="text-center">
                    {T("Please enter the 2-Factor Authentication code")}
                  </div>
                </div>
                <div className="mt-6 flex items-center justify-center">
                  <InputOtp
                    value={emailVerification.twoFa}
                    className="input-otp--box flex items-center justify-center"
                    onChange={(twoFa) => {
                      setEmailVerification((cur) => ({
                        ...cur,
                        twoFa: twoFa,
                      }));
                    }}
                  />
                </div>
                {emailVerification.twoFaError && (
                  <div className="mt-2 text-center text-red-500">
                    {emailVerification.twoFaError}
                  </div>
                )}
              </>
            )}
          </div>
          <div className="mt-[35px] flex justify-center items-center gap-5">
            {step === VERIFICATION_STEP.ENTER_OTP && (
              <>
                <FormButton
                  className={"black2 flex-1"}
                  onClick={() => onClose()}
                >
                  {verifyOptionParam.secondaryActionText ?? T("Cancel")}
                </FormButton>
                <FormButton
                  className={"primary2 flex-1"}
                  disabled={
                    loading || (emailVerification?.otp?.length ?? 0) < 6
                  }
                  onClick={onSubmitOtp}
                >
                  {verifyOptionParam.skipTwoFa
                    ? verifyOptionParam.actionText ?? T("Confirm")
                    : T("Next")}
                </FormButton>
              </>
            )}
            {hasTwoFa && step === VERIFICATION_STEP.ENTER_TWO_FA && (
              <>
                <FormButton
                  className={"black2 flex-1"}
                  onClick={() => onClose()}
                >
                  {T("Cancel")}
                </FormButton>
                <FormButton
                  className={"primary2 flex-1"}
                  disabled={
                    loading || (emailVerification?.twoFa?.length ?? 0) < 6
                  }
                  onClick={onSubmitTwoFa}
                >
                  {verifyOptionParam.actionText ?? T("Confirm")}
                </FormButton>
              </>
            )}
          </div>
        </ContentWrapper>
      </Modal.Container2>
    </Modal>
  );
};

export const ModalLineVerification = ({
  onClose,
  onChangeMethod,
  onSubmitOtp,
  onSubmitTwoFa,
  onResend,
  loadingResendOtp,
  loading
}) => {
  const {
    T,
    user,
    hasTwoFa,
    step,
    verifyOptionParam,
    lineVerification,
    setLineVerification,
  } = useVerificationMethodContext();

  const [countDown, setCountDown] = useState(
    lineVerification?.duration -
      Math.floor((Date.now() - lineVerification?.otpSentAt) / 1000)
  );
  const interval = useRef(null);

  useEffect(() => {
    if (lineVerification?.duration) {
      setCountDown(lineVerification?.duration);
      interval.current = setInterval(() => {
        const now = Date.now();
        if (
          now - lineVerification?.otpSentAt >=
          lineVerification?.duration * 1000
        ) {
          clearInterval(interval.current);
          setCountDown(0);
        }
        setCountDown(
          lineVerification?.duration -
            Math.floor((now - lineVerification?.otpSentAt) / 1000)
        );
      }, 1000);
      return () => clearInterval(interval.current);
    }
  }, [lineVerification?.duration, lineVerification?.otpSentAt]);

  const onChangeVerificationMethod = () => {
    onChangeMethod && onChangeMethod();
  };

  return (
    <Modal
      onClose={() => {
        onClose && onClose();
      }}
    >
      <Modal.ModalBg2 />
      <Modal.Container2
        width="550px"
        onClose={() => {
          onClose && onClose();
        }}
      >
        <ContentWrapper>
          <div className="text-center font-bold text-[18px] lg:text-[24px]">
            {step === VERIFICATION_STEP.ENTER_OTP && (
              <span>{T("LINE Verification")}</span>
            )}
            {hasTwoFa && step === VERIFICATION_STEP.ENTER_TWO_FA && (
              <span>{T("Verification")}</span>
            )}
          </div>
          <div className="mt-[15px] lg:mt-[20px]">
            {hasTwoFa && (
              <VerificationStepStyled className="flex items-center justify-center relative verification-step gap-6">
                <div
                  className={`flex flex-col items-center gap-2 verification-step__item px-5 z-10 ${
                    step === VERIFICATION_STEP.ENTER_OTP
                      ? "verification-step__item--active"
                      : ""
                  }`}
                >
                  {step === VERIFICATION_STEP.ENTER_OTP && (
                    <div className="w-8 h-8 rounded-full text-[16px]/[32px] font-bold text-white text-center verification-step__item__title">
                      1
                    </div>
                  )}
                  {lineVerification.verifiedOtp &&
                    step === VERIFICATION_STEP.ENTER_TWO_FA && (
                      <div className="w-8 h-8 rounded-full text-[16px]/[32px] font-bold text-white text-center flex items-center justify-center verification-step__item__title  verification-step__item__title--success">
                        <div className="w-2 h-4 border-b-2 border-r-2 border-white rotate-45 mb-1"></div>
                      </div>
                    )}
                  <div className="text-[16px]/[24px] font-bold verification-step__item__text">
                    {T("1st OTP")}
                  </div>
                </div>
                <div className="absolute bg-[#A3A3A3] h-[1px] w-[125px] top-4"></div>
                <div
                  className={`flex flex-col items-center gap-2 verification-step__item px-5 z-10 ${
                    step === VERIFICATION_STEP.ENTER_TWO_FA
                      ? "verification-step__item--active"
                      : ""
                  }`}
                >
                  <div className="w-8 h-8 rounded-full text-[16px]/[32px] font-bold text-white text-center verification-step__item__title">
                    2
                  </div>
                  <div className="text-[16px]/[24px] font-bold verification-step__item__text">
                    {T("2nd OTP")}
                  </div>
                </div>
              </VerificationStepStyled>
            )}
            {step === VERIFICATION_STEP.ENTER_OTP && (
              <>
                <div className="mt-6 flex items-center justify-between">
                  <div className="flex flex-col gap-1">
                    <span className="font-bold">
                      {T("Verification code sent to LINE")}
                    </span>
                    <span>{user?.lineDisplayName}</span>
                  </div>
                  <div>
                    {!loading &&
                      (countDown > 0 ? (
                        <div className="text-[#F12E11] text-[28px] font-bold">
                          {countDown}s
                        </div>
                      ) : (
                        <button
                          className="h-[32px] text-white bg-black px-4 py-1 rounded-full flex items-center justify-center gap-1 font-semibold text-[12px] hover:bg-[#333] transition-colors"
                          onClick={onResend}
                          disabled={loadingResendOtp}
                        >
                          {T("Resend")}
                        </button>
                      ))}
                  </div>
                </div>
                <div className="mt-4">
                  <div className="flex justify-between items-center gap-2">
                    <span className="">
                      {T("Check LINE messages for code")}
                    </span>
                    <a
                      // resource: https://developers.line.biz/en/docs/line-login/using-line-url-scheme/#opening-common-line-app-screens
                      href={`https://line.me/R/oaMessage/@${encodeURIComponent(
                        process.env.REACT_APP_LINE_ACCOUNT_BASIC_ID
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-[#00C200] text-white px-2 py-1 h-[24px] rounded-full flex items-center justify-center flex-shrink-0 gap-1 font-semibold text-[12px] text-decoration-none hover:text-white"
                    >
                      <LineIcon style={{ width: "24px", height: "24px" }} />
                      <span>{T("Open LINE")}</span>
                    </a>
                  </div>
                  <div className="mt-6">
                    <div className="text-center font-semibold text-[12px] lg:text-[14px]">
                      {T("Verification code")}
                    </div>
                  </div>
                  <div className="flex items-center justify-center mt-2">
                    <InputOtp
                      value={lineVerification.otp}
                      className="input-otp--box flex items-center justify-center"
                      onChange={(otp) => {
                        setLineVerification((cur) => ({
                          ...cur,
                          otp: otp,
                        }));
                      }}
                    />
                  </div>
                  {lineVerification.otpError && (
                    <div className="mt-2 text-center text-red-500">
                      {lineVerification.otpError}
                    </div>
                  )}
                </div>
                <div className="mt-6 text-center">
                  <span
                    className="underline cursor-pointer"
                    onClick={onChangeVerificationMethod}
                  >
                    {T("Not received code? Change verification method")}
                  </span>
                </div>
              </>
            )}
            {hasTwoFa && step === VERIFICATION_STEP.ENTER_TWO_FA && (
              <>
                <div className="mt-6">
                  <div className="text-center">
                    {T("Please enter the 2-Factor Authentication code")}
                  </div>
                </div>
                <div className="mt-4 flex items-center justify-center">
                  <InputOtp
                    value={lineVerification.twoFa}
                    className="input-otp--box flex items-center justify-center"
                    onChange={(twoFa) => {
                      setLineVerification((cur) => ({
                        ...cur,
                        twoFa: twoFa,
                      }));
                    }}
                  />
                </div>
                {lineVerification.twoFaError && (
                  <div className="mt-2 text-center text-red-500">
                    {lineVerification.twoFaError}
                  </div>
                )}
              </>
            )}
          </div>
          <div className="mt-[30px] mb-[20px] md:mb-[10px] flex justify-center items-center gap-[20px] border-t border-solid border-[#0000001A] pt-6">
            {step === VERIFICATION_STEP.ENTER_OTP && (
              <>
                <FormButton
                  className={"black2 flex-1"}
                  onClick={() => onClose()}
                >
                  {T("Cancel")}
                </FormButton>
                <FormButton
                  className={"primary2 flex-1"}
                  disabled={loading || (lineVerification?.otp?.length ?? 0) < 6}
                  onClick={onSubmitOtp}
                >
                  {verifyOptionParam.skipTwoFa
                    ? verifyOptionParam.actionText ?? T("Confirm")
                    : T("Next")}
                </FormButton>
              </>
            )}
            {hasTwoFa && step === VERIFICATION_STEP.ENTER_TWO_FA && (
              <>
                <FormButton
                  className={"black2 flex-1"}
                  onClick={() => onClose()}
                >
                  {T("Cancel")}
                </FormButton>
                <FormButton
                  className={"primary2 flex-1"}
                  disabled={
                    loading || (lineVerification?.twoFa?.length ?? 0) < 6
                  }
                  onClick={onSubmitTwoFa}
                >
                  {verifyOptionParam.actionText ?? T("Confirm")}
                </FormButton>
              </>
            )}
          </div>
        </ContentWrapper>
      </Modal.Container2>
    </Modal>
  );
};

const PasswordInput = ({
  value,
  placeholder = "",
  onInput = () => {},
  maxLength = 20,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <>
      <div className="mb-2">
        <div className={`w-full p-[1px] rounded-[10px] relative`}>
          <input
            value={value}
            type={showPassword ? "text" : "password"}
            className="h-[52px] w-full lg:max-w-full px-5 py-3
            bg-white rounded-[10px] text-black input-gradient border-[#E8E8E8] border-solid border-[1px]"
            placeholder={placeholder}
            onInput={onInput}
            maxLength={maxLength}
          />
          <div
            className={`w-6 h-6 flex justify-center ${
              showPassword ? "items-center" : "items-end"
            } absolute right-4 top-3.5 z-10 cursor-pointer`}
            onClick={() => toggleShowPassword()}
          >
            {showPassword && <IconOpenEye />}
            {!showPassword && <IconCloseEye />}
          </div>
        </div>
      </div>
    </>
  );
};

export const ModalConfirmPassword = ({
  loading,
  onClose,
  onSubmit,
  errorMessage,
}) => {
  const { T } = useVerificationMethodContext();
  const [formData, setFormData] = useState({});

  const btnVerifyOnClick = () => {
    if (formData.password) {
      onSubmit && onSubmit(formData.password);
    }
  };

  return (
    <Modal
      onClose={() => {
        onClose && onClose();
      }}
    >
      <Modal.ModalBg2
        onClose={() => {
          onClose && onClose();
        }}
      />
      <Modal.Container2
        width="450px"
        onClose={() => {
          onClose && onClose();
        }}
      >
        <ContentWrapper>
          <div className="text-center font-bold text-[18px] lg:text-[24px]">
            {T("Enter password")}
          </div>
          <div className="mt-[10px] lg:mt-[10px]">
            <div className="mt-[15px] lg:mt-[20px]">
              <div className="text-[14px] text-[#575657] mb-2">
                {T("Please enter your password to confirm connection request")}
              </div>
              <PasswordInput
                value={formData.password}
                placeholder={T("Please enter password")}
                onInput={(e) =>
                  setFormData({
                    ...formData,
                    password: e.target.value,
                  })
                }
              />
              {/* <div className="flex justify-end text-[#454B9F] text-[12px]">
                <a
                  href={env.SSO_FORGOT_PASSWORD_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {T("Forgot password?")}
                </a>
              </div> */}
              {errorMessage && (
                <div className="text-red-500 text-sm mt-2">{errorMessage}</div>
              )}
            </div>
            <div className="my-[32px] hidden md:block" />
            <div className="mt-[30px] mb-[20px] md:mb-[10px] flex flex-wrap md:flex-nowrap justify-center items-center gap-[20px]">
              <FormButton
                width={160}
                className={"primary2 w-full md:w-auto uppercase"}
                disabled={!formData.password || loading}
                onClick={btnVerifyOnClick}
              >
                {T("Verify")}
              </FormButton>
            </div>
          </div>
        </ContentWrapper>
      </Modal.Container2>
    </Modal>
  );
};

export const ModalConnectLineError = ({ loading, onClose, message }) => {
  const { T } = useVerificationMethodContext();

  return (
    <Modal
      onClose={() => {
        onClose && onClose();
      }}
    >
      <Modal.ModalBg2
        onClose={() => {
          onClose && onClose();
        }}
      />
      <Modal.Container2
        width="450px"
        onClose={() => {
          onClose && onClose();
        }}
      >
        <ContentWrapper>
          <div className="text-center font-bold text-[18px] lg:text-[24px]">
            {T("Connect LINE failed")}
          </div>
          <div className="mt-[10px] lg:mt-[10px]">
            <div className="mt-[15px] lg:mt-[20px]">
              <div className="flex items-center justify-center">
                <svg
                  width="90"
                  height="90"
                  viewBox="0 0 90 90"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle
                    cx="44.9996"
                    cy="45.148"
                    r="37.0855"
                    stroke="#D9D9D9"
                    strokeWidth="2.6178"
                  />
                  <path
                    d="M54.2692 35.877L35.7266 54.4197M35.7265 35.877L54.2692 54.4196"
                    stroke="#EA4848"
                    strokeWidth="3.9267"
                    strokeLinecap="round"
                  />
                </svg>
              </div>
              <div className="text-[16px] text-[#FF0017] font-bold text-center mt-6">
                {T("Unable to Connect to LINE")}
              </div>
              <div className="text-[14px] text-center mt-6">
                {message ??
                  T(
                    "We couldn't establish a connection with your LINE account at this time."
                  )}
              </div>
            </div>
            <div className="my-[32px] hidden md:block" />
            <div className="mt-[30px] mb-[20px] md:mb-[10px] flex flex-wrap md:flex-nowrap justify-center items-center gap-[20px]">
              <FormButton
                width={160}
                className={"primary2 w-full md:w-auto uppercase"}
                onClick={onClose}
              >
                {T("Ok")}
              </FormButton>
            </div>
          </div>
        </ContentWrapper>
      </Modal.Container2>
    </Modal>
  );
};
